/**
 * A person's profile: who may read it, and what is checked about the contact
 * details on it. Request of 4 October 2026 (second), section 7.
 *
 * The server's UserProfileRules (Dentist.Domain/Ceramica/UserProfileRules.cs)
 * is the same rule and the one that counts.
 */

import type { ActivityEntry } from '../activity/activityType';
import type { Stage } from '../kit/stage';
import type { Role } from '../access/role';
import type { Department } from './user';

/** `GET /users/{id}/profile`. */
export interface UserProfile {
  user: {
    id: string;
    name: string;
    username: string;
    title: string;
    role: Role;
    department: Department;
    isSupervisor: boolean;
    isIntakeOperator: boolean;
    disabled: boolean;
    avatarUrl: string | null;
    phone: string | null;
    email: string | null;
    createdAt: string;
    lastLoginAt: string | null;
  };
  range: ProfileRange;
  /** Null for a technician or CAD / CAM caller who has been given no screen of performance figures. */
  summary: { casesWorked: number; tasksCompleted: number; teethWorked: number; jobOrdersCreated: number } | null;
  current: { kitId: string; jobOrderNo: string; stage: Stage; running: boolean; startedAt: string }[];
  recent: ActivityEntry[];
}

export const PHONE_MAX = 32;
export const EMAIL_MAX = 120;

export const PROFILE_REFUSAL = {
  notYours: 'You can only open your own profile.',
  notYourPhoto: 'You can only change your own photo.',
  badPhone: 'Enter a phone number of digits, spaces and + ( ) - only.',
  badEmail: 'Enter an email address like name@example.com.',
} as const;

/**
 * The period a person's figures cover. All time first and by default: the lab's
 * request of 17 October 2026, item 4, "By default put all timeline sorts, to
 * all time, and then user can add more timelines."
 */
export type ProfileRange = 'all' | 'today' | 'week' | 'month';
export const PROFILE_RANGES: readonly ProfileRange[] = ['all', 'today', 'week', 'month'];

/** Your own, always; anybody's for management. */
export function mayReadProfile(callerId: string, userId: string, isAdmin: boolean): boolean {
  return callerId === userId || isAdmin;
}

/**
 * Whether a profile carries the work counts. Management always, and everybody
 * who works no department. A technician and CAD / CAM do not, their own
 * included, unless the lab has given them a screen of performance figures,
 * Staff productivity or My performance: then their profile does not hide what
 * that screen shows. The lab's answer of 11 October 2026: the tick always wins.
 * The server's UserProfileRules.SeesCounts.
 */
export function seesProfileCounts(callerRole: Role, isAdmin: boolean, holdsFigures = false): boolean {
  return isAdmin || holdsFigures || (callerRole !== 'tech' && callerRole !== 'cadcam');
}

/** Your own photograph, or anybody's for management. Role and department are management's alone. */
export function mayChangePhoto(callerId: string, userId: string, isAdmin: boolean): boolean {
  return callerId === userId || isAdmin;
}

/** Null when acceptable: blank means none, otherwise digits and phone punctuation, five digits at least. */
export function checkPhone(phone: string | null | undefined): string | null {
  const trimmed = (phone ?? '').trim();
  if (trimmed === '') return null;
  if (trimmed.length > PHONE_MAX) return PROFILE_REFUSAL.badPhone;
  const digits = trimmed.replace(/\D/g, '').length;
  return digits >= 5 && /^[0-9 +()-]+$/.test(trimmed) ? null : PROFILE_REFUSAL.badPhone;
}

/** Null when acceptable: blank means none, otherwise one @ with a dot after it. */
export function checkEmail(email: string | null | undefined): string | null {
  const trimmed = (email ?? '').trim();
  if (trimmed === '') return null;
  if (trimmed.length > EMAIL_MAX || trimmed.includes(' ')) return PROFILE_REFUSAL.badEmail;
  const at = trimmed.indexOf('@');
  return at > 0 && at === trimmed.lastIndexOf('@') && trimmed.indexOf('.', at) > at + 1 && !trimmed.endsWith('.')
    ? null
    : PROFILE_REFUSAL.badEmail;
}
